import { useTranslation } from "react-i18next"
import {
  ComponentProps,
  createContext,
  useCallback,
  use,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from "react"
import { Button } from "@repo/ui"
import { PeriodTypeView } from "@/types"
import {
  FixedTableCell,
  TableCell,
  TableCellContent,
  TableHead,
} from "@/components/ui/table"
import { useIntersectionObserver } from "@/lib/hooks/useIntersectionObserver/useIntersectionObserver"
import { cn } from "@/lib/utils"

type YearNavigationProps = {
  view: PeriodTypeView
  years: number[]
} & ComponentProps<"div">

export const YearNavigation = ({
  view,
  years,
  className,
  ...props
}: YearNavigationProps) => {
  const { t: commonT } = useTranslation(`common`)

  if (years.length > 0) {
    const { activeYear, scrollToYear, setActiveYear } = use(NavigationContext)
    return (
      <div
        {...props}
        className={cn(`flex items-center justify-between gap-2`, className)}
      >
        <div className="flex items-center gap-2">
          {view !== `yearly`
            ? years.map((year, index) => (
                <Button
                  id={index === 0 ? `fallback-year` : undefined}
                  data-year={year}
                  variant={`ghost`}
                  key={year}
                  size={`sm`}
                  className={cn(
                    `text-secondary rounded-none`,
                    year === activeYear
                      ? `border-b-[4px] border-primary text-normal`
                      : `border-b-[4px] border-transparent`
                  )}
                  onClick={() => {
                    scrollToYear(year)
                    setActiveYear(year)
                  }}
                >
                  {commonT(`table.fy`)}
                  {year}
                </Button>
              ))
            : null}
        </div>
      </div>
    )
  }

  return null
}

type TableHeaderProps = ComponentProps<typeof TableCell>

export const HeaderSpacer = (props: TableHeaderProps) => (
  <FixedTableCell
    id="year-spacer"
    {...props}
    className="border-r-transparent shadow-right-top-cell"
  >
    <TableCellContent type="input" className="h-10" />
  </FixedTableCell>
)
export const NavigationTH = ({ children, ...props }: TableHeaderProps) => {
  const { ref, isIntersecting, entry } = useIntersectionObserver({
    threshold: 0.3,
  })
  const [, startTransition] = useTransition()
  const isFirstMount = useRef(true)

  const { setActiveYear, tableRef, manualMode, setManualMode } =
    use(NavigationContext)

  /**
   * This effect is used to set the active year when the table is intersected.
   * My 2 cents: Nice design feature, not sure if it's worth the complexity.
   * Table is not the best place for this kind of interaction.
   * Strongly suggested to show 1 year at a time, instead of this scrolling mechanism.
   */
  useEffect(() => {
    const fallbackYear = tableRef?.current?.querySelector(
      `[data-use-fallback="true"]`
    ) as HTMLTableRowElement
    if (isIntersecting) {
      if (manualMode) {
        return () => setManualMode(false)
      }

      const getActiveYear = entry?.target.id.split(`-`)[1]
      if (getActiveYear) {
        startTransition(() => {
          setActiveYear(parseInt(getActiveYear, 10))
        })
      }
    } else if (isFirstMount.current) {
      // Only execute fallback on first mount
      if (fallbackYear && fallbackYear.dataset.year) {
        startTransition(() => {
          setActiveYear(parseInt(fallbackYear.dataset.year!, 10))
        })
      }
      isFirstMount.current = false
    }
  }, [
    isIntersecting,
    entry,
    setActiveYear,
    startTransition,
    tableRef,
    manualMode,
    setManualMode,
  ])

  return (
    <TableHead {...props} ref={ref}>
      {children}
    </TableHead>
  )
}

export const NavigationContext = createContext<{
  activeYear: number
  setActiveYear: (year: number) => void
  scrollToYear: (year: number) => void
  tableRef: React.RefObject<HTMLTableElement | null> | null
  manualMode: boolean
  setManualMode: (mode: boolean) => void
}>({
  activeYear: 0,
  setActiveYear: () => {},
  scrollToYear: () => {},
  tableRef: null,
  manualMode: false,
  setManualMode: () => {},
})

export const NavigationProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const [activeYear, setActiveYear] = useState(0)
  const tableRef = useRef<HTMLTableElement>(null)
  const [manualMode, setManualMode] = useState(false)
  const scrollToYear = useCallback(
    (year: number) => {
      setManualMode(true)
      let yearElement = tableRef.current?.querySelector(
        `#year-${year}`
      ) as HTMLTableRowElement

      if (!yearElement) {
        yearElement = tableRef.current?.querySelector(
          `[data-use-fallback="true"]`
        ) as HTMLTableRowElement
      }

      const yearSpacer = tableRef.current?.querySelector(
        `#year-spacer`
      ) as HTMLTableRowElement

      let defaultOffset = 320

      if (yearSpacer) {
        defaultOffset = yearSpacer.getBoundingClientRect().width
      }

      if (tableRef.current?.parentElement) {
        tableRef.current.parentElement.scrollLeft =
          yearElement.offsetLeft - defaultOffset
      }
    },
    [tableRef]
  )

  const value = useMemo(
    () => ({
      activeYear,
      setActiveYear,
      scrollToYear,
      tableRef,
      manualMode,
      setManualMode,
    }),
    [activeYear, scrollToYear, manualMode]
  )

  return <NavigationContext value={value}>{children}</NavigationContext>
}
